import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import type { AuthSession } from './authSession';

interface SessionRequest extends InternalAxiosRequestConfig {
  reauthentication?: boolean;
  authGeneration?: number;
  authToken?: string;
  authRetried?: boolean;
  releaseSignal?: () => void;
}

export interface ReauthenticationRequest extends AxiosRequestConfig { reauthentication: true }

export class ApiRequestError extends Error {
  readonly apiError: { statusCode: number; error: string; message: string; details?: unknown };
  readonly response: { status: number; data: ApiRequestError['apiError'] };
  constructor(public readonly statusCode: number, body?: Record<string, unknown>) {
    super(statusCode === 401 ? 'Authentication required' : statusCode === 403 ? 'Permission is required' :
      statusCode >= 500 ? 'Service is unavailable' :
      typeof body?.message === 'string' ? body.message : 'Request failed');
    this.name = 'ApiRequestError';
    this.apiError = { statusCode, message: this.message,
      error: typeof body?.error === 'string' && /^[A-Z_]+$/.test(body.error) ? body.error : 'REQUEST_FAILED',
      ...(statusCode === 422 && body?.details ? { details: body.details } : {}),
    };
    this.response = { status: statusCode, data: this.apiError };
  }
}

export function bindSessionClient(client: AxiosInstance, session: AuthSession): AxiosInstance {
  const baseURL = client.defaults.baseURL;
  delete client.defaults.headers.common.Authorization;
  client.interceptors.request.use(async (config: SessionRequest) => {
    if (!config.url?.startsWith('/') || config.url.startsWith('//') || /[\\\r\n]/.test(config.url) ||
      config.baseURL !== baseURL) throw new ApiRequestError(400);
    if (config.authGeneration !== undefined) session.assertCurrent(config.authGeneration);
    await session.bootstrap();
    const token = session.accessToken();
    if (!token) throw new ApiRequestError(401);
    config.authGeneration = session.getSnapshot().generation;
    config.authToken = token;
    config.headers.set('Authorization', `Bearer ${token}`);
    config.headers.set('X-ERP-CSRF', '1');
    config.withCredentials = true;
    const controller = new AbortController();
    const signals = [config.signal, session.requestSignal()];
    const abort = () => controller.abort();
    signals.forEach(signal => {
      if (signal?.aborted) abort();
      else signal?.addEventListener?.('abort', abort, { once: true });
    });
    config.releaseSignal = () => signals.forEach(signal => signal?.removeEventListener?.('abort', abort));
    config.signal = controller.signal;
    return config;
  });
  client.interceptors.response.use(response => {
    const config = response.config as SessionRequest;
    config.releaseSignal?.();
    session.assertCurrent(config.authGeneration!);
    return response;
  }, async error => {
    const config = error.config as SessionRequest | undefined;
    config?.releaseSignal?.();
    if (axios.isCancel(error)) throw error;
    if (config?.authGeneration !== undefined) session.assertCurrent(config.authGeneration);
    const status = error.response?.status || error.statusCode || 0;
    if (status === 401 && config?.authGeneration !== undefined && !config.reauthentication) {
      if (!config.authRetried) {
        config.authRetried = true;
        await session.refresh(config.authGeneration, config.authToken);
        session.assertCurrent(config.authGeneration);
        return client.request(config);
      }
      session.clear();
    }
    throw new ApiRequestError(status, error.response?.data);
  });
  return client;
}
